import React, { useCallback, useEffect, useState } from "react";
import { ApolloProvider } from "@apollo/client";
import mixpanel from "mixpanel-browser";
import { useRecoilValue } from "recoil";
import { useAtom } from "jotai";
import Routes from "./Routes";
import { client } from "./common/apollo/ApolloClient";
import { getFirebaseInstance } from "./common/firebase";
import { onAuthStateChanged } from "firebase/auth";
import AuthContext from "./common/contexts/auth";
import TrackingContext from "./common/contexts/tracking";
import "./common/utils/facebook";
import {
  organizationState,
  authState,
  authErrorState,
} from "./atoms";

import {
  logout,
  onWindowBlurListener,
  visibilityChangeListner,
} from "./common/firebase/firebase";
import {
  deleteCookie,
  getCookie,
} from "./utils";
import ScrollProvider from "./common/contexts/scroll";
import { useOrganization, useUserOrganizationPermissionData } from "./hooks";
import { commonAuthDeleteCookies, commonRemoveAuthEvents } from "./common/auth/utils";
import ToastContextWithProvider, { useToaster } from "./common/contexts/toast";
import PageLoader from "./components/page-loader";
import Logger from "./common/logger";
import { authStatusCookie, userDisplayNameCookie } from "./utils/cookies";
import { firebaseTokenAPI } from "./common/services";

const App = () => {

  const { showToaster } = useToaster()
  const firebase = getFirebaseInstance();

  // local states
  const [authLoader, setAuthLoader] = useState(true);

  // global states
  // jotai
  const [auth, setAuth] = useAtom(authState);
  const [authError, setAuthError] = useAtom(authErrorState)

  //recoil
  const organization = useRecoilValue(organizationState);

  // hooks
  const { fetchUserOrgPermission } = useUserOrganizationPermissionData();
  const { refreshOrganization } = useOrganization();

  const [tracking] = useState(
    (process.env.REACT_APP_ENV === "production" || process.env.REACT_APP_ENV === "qa") &&
      process.env.REACT_APP_TRACKING_ID
      ? mixpanel
      : null
  );

  const commonRemoveSessionItems = useCallback((logInMode) => {
    setAuth({
      user: null,
      isAuthenticated: false,
    });

    commonAuthDeleteCookies()
  },
    [setAuth]
  );

  const afterSuccessLogIn = useCallback(async () => {

      const authStatus = getCookie(authStatusCookie);

      if (authStatus === "true") {
       await fetchUserOrgPermission(tracking);

       document.addEventListener("visibilitychange", visibilityChangeListner);
       window.addEventListener("blur", onWindowBlurListener);
       setAuthLoader(false)
      } else {
        setAuthLoader(false)
      } 
    
    tracking && tracking.track("Login");
    setAuthError(defaultError)
  }, [tracking, fetchUserOrgPermission, setAuthError]);

  useEffect(() => {
    // removing auto logout event
    return () => {
      commonRemoveAuthEvents()
    };
  }, []);

  useEffect(() => {

    afterSuccessLogIn()
    
  // have not included afterSuccessLogIn because of multilpe calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const onAuthStateChange = useCallback(async (user) => {
    if (auth.user) return auth.user;

    try {
      setAuthError(defaultError);
      const idToken = await user.getIdToken(false);

      const sendTokenRes = await firebaseTokenAPI(idToken)

      if (sendTokenRes.data.status) {
        afterSuccessLogIn()
        
      } else {
        throw new Error(sendTokenRes?.data?.message || 'Error while login')
      }
      
    } catch (error) {
      Logger.error('onAuthStateChange error', {
        error,
        context: {
          user,
        }
      });
      deleteCookie(userDisplayNameCookie);
      window.indexedDB.deleteDatabase("firebaseLocalStorageDb");
      logout(true);
      setAuthError({ google: error?.response?.data?.message || error?.message || 'Some error occured. Please try again.' })
    }
  }, [afterSuccessLogIn, auth.user, setAuthError]);

  // google sign in
  useEffect(() => {
    const logInMode = getCookie("__login_mode");
    const unsubscribe = onAuthStateChanged(firebase.auth, async (user) => {
      if (user) {
        // User signed in
        await onAuthStateChange(user);
      } else if (logInMode === "google") {
        commonRemoveSessionItems("google");
      }
    });

    return () => {
      unsubscribe()
    }
    // have only included onAuthStateChange dependecy to stop this hook from runnig more than once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAuthStateChange]);

  const connectToAccHandler = useCallback(async (status, message) => {
    if (status) {
      await refreshOrganization();
    }
    showToaster(`connect-to-acc-toast`, message, status)
  },
    [refreshOrganization, showToaster]
  );

  //reading event after success of autodesk login from popup window
  useEffect(() => {
    const messageRes = (message) => {
      const data = message.data;
      
      if (!data.service) {
        return;
      }

      const loginTypes = ['success-login', 'success-signup', 'success-otp']

      if (loginTypes.includes(data.service)) {
        afterSuccessLogIn()
      }

      if (data.service === "connect-to-acc") {
        connectToAccHandler(data.status, data.message);
      }
    };
    window.addEventListener("message", messageRes);

    return () => {
      window.removeEventListener("message", messageRes);
    };

    // have only included onAuthStateChange dependecy to stop this hook from runnig more than once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectToAccHandler]);
  
  if (
    (authLoader || (auth.isAuthenticated && !organization?.id))
  ) {
    return (
      <PageLoader />
    );
  }

  return (
    <>
      <TrackingContext.Provider value={tracking}>
        <AuthContext.Provider value={auth}>
          <ApolloProvider client={client}>
            <ScrollProvider>
              <Routes authError={authError} />
            </ScrollProvider>
          </ApolloProvider>
        </AuthContext.Provider>
      </TrackingContext.Provider>
    </>
  );
};

const defaultError = {
  google: "",
  autodesk: ""
}

const AppWithToast = () => (
  <ToastContextWithProvider>
    <App />
  </ToastContextWithProvider>
);

export default AppWithToast;
